import * as $ from "jquery";
import * as _ from "lodash";
import { Utils } from "../../../../common/utils";
import { DashboardChannel } from "../../../../models/DashboardChannel";
import { AggregationWidgetManager } from "../AggregationWidgetManager";
import { BaseDashboardWidgetView, BaseDashboardWidgetViewOptions } from "../_BaseWidgetView";
import { CHART_AGGREGATION_PERIOD } from "../chart/ChartHelper";
import { WIDGET_DIMENSIONS } from "../../../../common/widgetLayout";

declare var App: any;

export abstract class BaseSimpleWidget extends BaseDashboardWidgetView {
    protected lastRenderedChannelIds: number[] = [];
    protected channelsSelected: number[] = [];
    protected lastFilterValue: string = null;
    protected template = _.template(require("templates/dashboard/widgets/widgetWrapper.html"));
    protected aggregationWidgetManager: AggregationWidgetManager;

    constructor(options: BaseDashboardWidgetViewOptions) {
        super(
            <any>_.merge(
                {
                    tagName: "div",
                    className: "widget widget-w-1", // Default to small, will be updated in render
                    events: {
                        "click .simple-widget-channel": "handleChannelClick",

                        // Widget related buttons
                        "click .w-settings .remove": "handleRemoveWidgetClick",
                        "click .w-delete-widget .w-cancel": "handleRemoveWidgetCancelClick",
                        "click .w-delete-widget .w-confirm": "handleRemoveWidgetConfirmClick",
                        "click .w-settings .gear": "handleEditWidgetClick",
                        "click .w-settings .clone": "handleCloneWidget",
                    },
                },
                options
            )
        );

        this.channelsSelected = [];
        this.lastRenderedChannelIds = [];
        this.lastFilterValue = null;
        this.aggregationWidgetManager = new AggregationWidgetManager(options);

        this.listenTo(this.options.dashboardChannelCollection, "sync update", this.renderChannels.bind(this, true));
        this.listenTo(this.options.widget, "change", this.render.bind(this));
        this.listenTo(
            App.Bus,
            "dashboard:filter:notify",
            _.debounce((filterValue) => {
                this.lastFilterValue = filterValue ? filterValue.toLowerCase() : null;
                this.renderChannels();
            }, 500)
        );

        this.listenTo(App.Bus, "dashboard:clearAllSelections", this.clearAllSelections);

        // Resubmit our state if another widget wants us to
        this.listenTo(
            App.Bus,
            "dashboard:category:" + this.options.categoryId + ":widgets:getStateFromRange",
            this.resendAllSelectedChannelSignals
        );
        this.listenTo(
            App.Bus,
            "dashboard:category:all:widgets:getStateFromRange",
            this.resendAllSelectedChannelSignals
        );
        
        // Set initial widget size
        this.updateWidgetSize();
    }

    render() {
        // Only re-render the entire widget if it's the first render or if the widget structure needs to change
        if (!this.$('.w-settings').length) {
            this.$el.html(
                this.template({
                    dashboardId: this.options.dashboardId,
                    permissionCollection: this.options.permissionCollection,
                    widget: this.options.widget,
                    editable: this.options.editable,
                    movable: this.options.movable,
                    cloneable: this.options.cloneable,
                    deletable: this.options.deletable,
                })
            );
            this.$el.attr("data-widget-id", this.options.widget.get("id"));
        }
        
        this.updateWidgetSize();
        this.renderChannels();

        return this;
    }

    private updateWidgetSize() {
        // Get widget size from parameters, default to small
        const widgetSize = this.options.widget?.getParamValue('WIDGET_SIZE', 'small');
        const sizeClass = widgetSize === 'medium' ? 'widget-w-2' : 'widget-w-1';
        
        // Remove existing size classes and add the new one
        this.$el.removeClass('widget-w-1 widget-w-2');
        this.$el.addClass(sizeClass);
    }

    protected abstract renderChannels();

    protected handleChannelClick(e: JQuery.UIEventBase) {
        var elem = $(e.currentTarget);
        var channelId = Number(elem.attr("data-channel-id"));
        if (_.includes(this.channelsSelected, channelId)) {
            this.channelsSelected = _.without(this.channelsSelected, channelId);
            elem.removeClass("clicked");
            this.sendChannelDeselectSignal(channelId);
        } else {
            this.channelsSelected.push(channelId);
            elem.addClass("clicked");
            this.sendChannelSelectSignal(channelId);
        }
    }

    protected resendAllSelectedChannelSignals() {
        _.each(_.intersection(this.lastRenderedChannelIds, this.channelsSelected), (id) => {
            this.sendChannelSelectSignal(id);
        });
    }

    protected sendChannelSelectSignal(channelId) {
        App.Bus.trigger("dashboard:category:" + this.options.categoryId + ":chart:addChannel", channelId);
        App.Bus.trigger("dashboard:category:all:chart:addChannel", channelId);
    }

    protected clearAllSelections() {
        var selected = this.channelsSelected.slice();
        _.each(selected, (channelId) => {
            this.sendChannelDeselectSignal(channelId);
        });
        this.channelsSelected = [];
        this.$(".simple-widget-channel").removeClass("clicked");
    }

    protected sendChannelDeselectSignal(channelId) {
        App.Bus.trigger("dashboard:category:" + this.options.categoryId + ":chart:removeChannel", channelId);
        App.Bus.trigger("dashboard:category:all:chart:removeChannel", channelId);
    }
    
    protected syncChannelsWithChart(callback?: () => void) {
        // Query the default chart for which channels are currently graphed
        // This restores the clicked state when switching tabs
        var self = this;
        
        // Use requestAnimationFrame to ensure DOM is ready, then query chart immediately
        requestAnimationFrame(() => {
            App.Bus.trigger('dashboard:category:all:chart:getChannels', (chartChannelIds: number[]) => {
                if (!chartChannelIds || chartChannelIds.length === 0) {
                    // No channels in chart, clear selection
                    this.channelsSelected = [];
                    if (callback) callback();
                    return;
                }
                
                // Get all channel IDs that are rendered in this widget
                var widgetChannelIds = this.lastRenderedChannelIds || [];
                
                // Find channels that are in both the chart and this widget
                var channelsToSelect = _.intersection(widgetChannelIds, chartChannelIds);
                
                // Update channelsSelected to match what's in the chart
                this.channelsSelected = channelsToSelect;
                
                // Call callback if provided (for widgets that need to update visual state)
                if (callback) callback();
            });
        });
    }

    protected handleRemoveWidgetConfirmClick() {
        // Deselect all channels when a widget is removed
        _.each(this.channelsSelected, (selectedId) => {
            this.sendChannelDeselectSignal(selectedId);
        });

        super.handleRemoveWidgetConfirmClick();
    }

    protected handleEditWidgetClick(eventTrigger) {
        this.options.widget.once("sync", () => {
            this.options.dashboardChannelCollection.once("sync", () => {
                // Check if any channels were removed that were selected
                var channels = this.options.dashboardChannelCollection.where({
                    dashboard_widget_id: this.options.widgetId,
                });
                _.each(this.channelsSelected, (selectedId: number) => {
                    var selectedChannel = _.find(channels, (dashboardChannel) => {
                        return dashboardChannel.get("dashboard_channel_id") === selectedId;
                    });

                    if (!selectedChannel) this.sendChannelDeselectSignal(selectedId);
                });
                this.renderChannels();
            });
        });
        this.options.manager.createModifyDashboardWidgetModal(this.options.widget);
    }

    getState(channel) {
        var value = channel.get("channel_converted_value");
        var limit_crit_high = channel.get("limit_crit_high");
        var limit_crit_low = channel.get("limit_crit_low");
        var limit_warning_high = channel.get("limit_warning_high");
        var limit_warning_low = channel.get("limit_warning_low");

        if (!_.isNumber(value)) {
            return ["green", "success", null];
        } else if (limit_crit_high !== null && value >= limit_crit_high) {
            return ["red", "danger", "up"];
        } else if (limit_crit_low !== null && value <= limit_crit_low) {
            return ["red", "danger", "down"];
        } else if (limit_warning_high !== null && value >= limit_warning_high) {
            return ["yellow", "warning", "up"];
        } else if (limit_warning_low !== null && value <= limit_warning_low) {
            return ["yellow", "warning", "down"];
        } else {
            return ["green", "success", null];
        }
    }

    getStateFromRegularThresholds(channel: DashboardChannel) {
        var numChannels = this.options.dashboardChannelCollection.filter((dashboardChannel) => {
            return dashboardChannel.get("dashboard_widget_id") == this.options.widgetId;
        }).length;
        var thresholdAddon = numChannels >= 7 || numChannels == 3 ? "" : " Threshold";

        var thresholds = this.getThresholdsLimitsFromRangesList(channel.get("rangesList"));
        var value = channel.get("channel_converted_value");
        var color = Utils.colors.green;
        var name = "Good";
        var arrow = "";

        if (_.isNumber(value)) {
            if (thresholds.critHigh !== null && value >= thresholds.critHigh) {
                color = Utils.colors.red;
                name = "Above" + thresholdAddon;
                arrow = "up";
            } else if (thresholds.critLow !== null && value <= thresholds.critLow) {
                color = Utils.colors.red;
                name = "Below" + thresholdAddon;
                arrow = "down";
            } else if (thresholds.warningHigh !== null && value >= thresholds.warningHigh) {
                color = Utils.colors.yellow;
                name = "Above" + thresholdAddon;
                arrow = "up";
            } else if (thresholds.warningLow !== null && value <= thresholds.warningLow) {
                color = Utils.colors.yellow;
                name = "Below" + thresholdAddon;
                arrow = "down";
            }
        }

        return {
            color: color,
            name: name,
            arrow: arrow,
        };
    }

    getStateFromRange(channel: DashboardChannel) {
        const range = channel.getRange();
        let color = Utils.colors.green;
        let name = "Good";
        let arrow = "";
        if (range) {
            color = range.get("color");
            name = range.get("name");

            // Check if description contains either 'above' or 'below'
            // to set the arrow value
            const description = range.get("description");
            if (/below/.test(description)) {
                arrow = "down";
            } else if (/above/.test(description)) {
                arrow = "up";
            }
        }

        return {
            color: color,
            name: name,
            arrow: arrow,
        };
    }

    getThresholdsLimitsFromRangesList(rangesList) {
        var critLow = <any>_.find(rangesList, { name: "Critical Low" });
        var warningLow = <any>_.find(rangesList, { name: "Warning Low" });
        var warningHigh = <any>_.find(rangesList, { name: "Warning High" });
        var critHigh = <any>_.find(rangesList, { name: "Critical High" });

        return {
            critLow: critLow ? critLow.max : null,
            warningLow: warningLow ? warningLow.max : null,
            warningHigh: warningHigh ? warningHigh.min : null,
            critHigh: critHigh ? critHigh.min : null,
        };
    }
}

export class SimpleWidget extends BaseSimpleWidget {
    protected channelsTemplate = _.template(require("templates/dashboard/widgets/simplewidget/channels.html"));

    protected renderChannels() {
        const rawUseCustomLabelsConfig = this.options.widget.getParamValue("USE_CUSTOM_LABELS", true);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        const useCustomLabels = !!Number(rawUseCustomLabelsConfig);

        var aggPeriod = this.options.widget.getParamValue("CHART_AGGREGATION_PERIOD", "");
        if (aggPeriod && aggPeriod != CHART_AGGREGATION_PERIOD.NONE) {
            var d = this.aggregationWidgetManager.checkAggregation();
        } else {
            var d = $.Deferred().resolve();
        }

        $.when(d).then((valueOverrides) => {
            var channels = <DashboardChannel[]>(
                this.options.dashboardChannelCollection.where({ dashboard_widget_id: this.options.widgetId })
            );
            var channelsToRender = [];
            var sorted = _.sortBy(channels, (c) => {
                return c.get("dashboard_channel_order");
            });

            var values = {};
            _.each(channels, (c) => {
                var id = c.get("dashboard_channel_id");
                if (valueOverrides) {
                    var lastValue = _.last(_.last(valueOverrides[id]));
                    const lastValueFormatted = Utils._getFormattedValue(
                        lastValue,
                        c.get("sensor_type"),
                        c.get("sensor_version"),
                        Utils._getFormatDecimals(c.get("channel_decimals"), c.get("channel_converted_value")),
                        c.get("channel_conversion_function_name")
                    );

                    const formattedValue = Utils.validateFormattedValue(lastValueFormatted);
                    values[id] = formattedValue;
                } else {
                    const value = c.get("channel_formatted_value");
                    const formattedValue = Utils.validateFormattedValue(value);
                    values[id] = formattedValue;
                }
            });

            _.each(sorted, (channel: any) => {
                var lastSeenStr = Utils.Time.getChannelLastSeenString(channel.get("channel_last_timestamp"));
                var lastSeenColor = Utils.Time.getChannelLastSeenColor(channel.get("channel_last_timestamp"));
                var isChannelLastSeenStale = Utils.Time.isChannelLastSeenStale(channel.get("channel_last_timestamp"));
                var channelLastSeenOpacity = Utils.Time.getChannelLastSeenOpacity(channel.get("channel_last_timestamp"));
                var state = this.getStateFromRange(channel);
                // Use default name or custom name based on configuration
                let channelName = channel.get("dashboard_channel_name");
                if (!useCustomLabels)
                    channelName = `${channel.get("module_location")} - ${channel.get("channel_name")}`;

                var id = channel.get("dashboard_channel_id");
                //clicked change
                var clicked = _.includes(this.channelsSelected, id);
                channelsToRender.push({
                    name: channelName,
                    unit: channel.get("channel_unit"),
                    value: values[id],
                    color: clicked ? "#FFF" : state.color,
                    fontColor: clicked ? "#FFF" : "#333333",
                    backgroundColor: clicked ? state.color : "#F8F8F8",
                    statusColor: state.color,
                    state: state.name,
                    thresholdCrossed: state.arrow,
                    lastSeen: lastSeenStr,
                    lastSeenColor: lastSeenColor,
                    isChannelLastSeenStale: isChannelLastSeenStale,
                    channelLastSeenOpacity: channelLastSeenOpacity,
                    icon: this.getIcon(channel),
                    id: id,
                    clicked: clicked,
                });
            });

            // Filter and select/deselect channels based on the current dashboard filter
            channelsToRender = _.filter(channelsToRender, (c) => {
                var showChannel = !this.lastFilterValue || c.name.toLowerCase().indexOf(this.lastFilterValue) != -1;

                // Deselect Channels that are filtered
                if (!showChannel && _.includes(this.channelsSelected, c.id)) {
                    this.sendChannelDeselectSignal(c.id);
                }

                // Re select channels that are not or no longer filtered
                if (showChannel && _.includes(this.channelsSelected, c.id)) {
                    this.sendChannelSelectSignal(c.id);
                }

                return showChannel;
            });

            var agg_period = this.options.widget.getParamValue("CHART_AGGREGATION_PERIOD", "");
            agg_period = this.aggregationWidgetManager.getPeriodLabel(agg_period);
            var agg_types = this.options.widget.getParamValue("CHART_AGGREGATION_TYPE", "").split(",");
            agg_types = _.map(agg_types, (t) => this.aggregationWidgetManager.getTypeLabel(String(t)));
            if (channelsToRender.length) {
                this.$el.removeClass("hidden");
                this.renderChannelsTemplate({
                    channels: channelsToRender,
                    widget: this.options.widget,
                    widgetSize: this.options.widget.getParamValue("WIDGET_SIZE", "small"),
                    agg_type: this.options.widget.getParamValue("CHART_AGGREGATION_TYPE", ""),
                    agg_period: this.options.widget.getParamValue("CHART_AGGREGATION_PERIOD", ""),
                    aggregationFuncLabel: agg_types,
                    aggregationPeriodLabel: agg_period,
                });

                // Sync channelsSelected with channels currently in the default chart
                // This restores clicked state when switching tabs
                this.syncChannelsWithChart(() => {
                    // Re-apply the clicked class after sync updates channelsSelected
                    this.$(".simple-widget-channel").removeClass("clicked");
                    _.each(this.channelsSelected, (channelId) => {
                        this.$(`[data-channel-id="${channelId}"]`).addClass("clicked");
                    });
                });

                // Initialize Bootstrap tooltips for channel names
                (<any>this.$('.w-channel [title]')).tooltip({
                    placement: 'top',
                    trigger: 'hover'
                });

                // Apply height to the .body.w-channels container based on channel count
                // Uses shared helper function for consistency across widgets
                const channelCount = channelsToRender.length;
                const calculatedHeight = WIDGET_DIMENSIONS.getSimpleWidgetHeight(channelCount);
                
                // Only set the height programmatically - flex styles are now in CSS
                this.$('.body.w-channels').css('height', calculatedHeight + 'px');
            } else {
                this.$el.addClass("hidden");
            }

            var renderedChannelIds = <number[]>_.map(channelsToRender, "id");
            if (
                this.lastRenderedChannelIds.length !== renderedChannelIds.length ||
                _.difference(this.lastRenderedChannelIds, renderedChannelIds).length !== 0
            ) {
                window["App"].Bus.trigger("dashboard:category:" + this.options.categoryId + ":layout");
            }

            this.lastRenderedChannelIds = renderedChannelIds;
        });
    }

    protected renderChannelsTemplate(options) {
        this.$(".w-rows").html(this.channelsTemplate(options));
    }

    protected getIcon(channel) {
        var type = channel.get('sensor_type');
        var pos = channel.get('channel_position');
        var version = channel.get('sensor_version');
        var _default = 'fa-regular fa-gauge';

        switch (type) {
            case 1:
                if (version <= 4) {
                    switch (pos) {
                        case 1:
                            // Voltage
                            return "fa-regular fa-bolt";
                        case 2:
                            // Temp
                            return "fa-regular fa-temperature-three-quarters";
                        case 3:
                            // Humidity
                            return "fa-regular fa-water";
                    }
                } else if (version >= 5) {
                    switch (pos) {
                        case 1:
                            // Temp
                            return "fa-regular fa-temperature-three-quarters";
                        case 2:
                            // Humidity
                            return "fa-regular fa-water";
                        case 3:
                            // Voltage
                            return "fa-regular fa-bolt";
                    }
                }
                return _default;
            case 10:
                // Pulse Power
                return "fa-regular fa-bolt";
            case 11:
                return "fa-regular fa-bolt";
            case 12:
                switch (pos) {
                    // Temp
                    case 1:
                        return "fa-regular fa-temperature-three-quarters";
                    // Hum
                    case 2:
                        return "fa-regular fa-water";
                    // CO2
                    case 3:
                        return "fa-regular fa-wind";
                }
                return _default;
            case 14:
                // Light exposure
                return "fa-regular fa-lightbulb";
            case 15:
                // water pulse
                return "fa-regular fa-droplet";
            case 16:
                // Submeter kwh
                return "fa-regular fa-bolt";
            case 17:
                switch (pos) {
                    case 1:
                        // Temp
                        return "fa-regular fa-temperature-three-quarters";
                    case 2:
                        // Humidity
                        return "fa-regular fa-water";
                }
                return _default;
            case 18:
                // Contact closure
                return "fa-regular fa-cog";
            case 19:
                // gas pulse
                return "fa-regular fa-gauge";
            case 20:
                if (pos === 1) {
                    return "fa-regular fa-lightbulb-on";
                } else if (pos == 2) {
                    return "fa-regular fa-volume";
                }
                return _default;
            // Modbus
            case 22:
            case 23:
                return "fa-regular fa-bolt";
            // 4-20mA
            case 24:
            // termistor
            case 25:
                return "fa-regular fa-temperature-three-quarters";
            //volt
            case 26:
                return "fa-regular fa-bolt";
            case 34: // IAQ
                switch (pos) {
                    case 1:
                        // Temp
                        return "fa-regular fa-temperature-three-quarters";
                    case 2:
                        // Humidity
                        return "fa-regular fa-water";
                }
                return "fa-regular fa-wind";
            // Dual Volt/Thermistor
            case 37:
                switch (pos) {
                    case 1:
                    case 2:
                        // Volt
                        return "fa-regular fa-bolt";
                    case 3:
                    case 4:
                        // Thermistor
                        return "fa-regular fa-temperature-three-quarters";
                }
                return _default;
            // Dual Pulse/Contact Closure
            case 38:
                switch (pos) {
                    case 3:
                    case 4:
                        // Contact Closure
                        return "fa-regular fa-cog";
                }
                return _default;
            default:
                return _default;
        }
    }
}


// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/simplewidget/SimpleWidget.ts